import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authToken } from '../../api/apiBase';
import { PHASES_RETAINED_READ_TIMEOUT_MS } from '../../lib/mackPhasesEvidence';
import { readUsdGdpHistory, USD_GDP_RETAINED_SOURCES, UsdGdpEvidenceKind,
  UsdGdpHistoryPage, UsdGdpEvidenceTimeoutError, usdGdpReadFailure } from '../../lib/mackUsdGdpEvidence';
import { C, MONO } from './tokens';

interface Flight { controller: AbortController; deadline: number }
interface Session { token: string; active: boolean; flight?: Flight }
const show = (value: unknown, fallback = 'Not exposed by source') => value === null || value === undefined || value === '' ? fallback : String(value);
const buttonStyle: React.CSSProperties = { background: C.chipOffBg, border: `1px solid ${C.chipOffBd}`, borderRadius: 6,
  color: C.amber, padding: '5px 8px', cursor: 'pointer', font: 'inherit', fontSize: 11 };

function RetainedSeries({ kind, token }: { kind: UsdGdpEvidenceKind; token?: string | null }) {
  const effectiveToken = token || authToken();
  const source = USD_GDP_RETAINED_SOURCES[kind];
  const session = useRef<Session | null>(null);
  const [snapshot, setSnapshot] = useState<{ token: string; pages: UsdGdpHistoryPage[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pages = snapshot?.token === effectiveToken ? snapshot.pages : [];
  const rows = pages.flatMap(page => page.rows);
  const latest = rows[0];
  const last = pages[pages.length - 1];
  const load = useCallback(async (offset = 0) => {
    const current = session.current;
    if (!current || !current.active || current.token !== effectiveToken || current.flight) return;
    const controller = new AbortController();
    const isCurrent = () => current.active && session.current === current && current.flight === flight;
    const finish = () => {
      window.clearTimeout(flight.deadline);
      if (isCurrent()) { current.flight = undefined; setBusy(false); }
    };
    const flight: Flight = { controller, deadline: window.setTimeout(() => {
      if (!isCurrent()) return;
      setError(usdGdpReadFailure(new UsdGdpEvidenceTimeoutError())); finish(); controller.abort();
    }, PHASES_RETAINED_READ_TIMEOUT_MS) };
    current.flight = flight; setBusy(true); setError('');
    try {
      const page = await readUsdGdpHistory(kind, current.token, controller.signal, offset);
      if (isCurrent()) setSnapshot(previous => ({ token: current.token,
        pages: offset && previous?.token === current.token ? [...previous.pages, page] : [page] }));
    } catch (cause) { if (isCurrent()) setError(usdGdpReadFailure(cause)); }
    finally { finish(); }
  }, [effectiveToken, kind]);
  useEffect(() => {
    const current: Session = { token: effectiveToken, active: true }; session.current = current;
    setSnapshot(null); setError(''); void load();
    return () => { current.active = false; if (current.flight) { window.clearTimeout(current.flight.deadline); current.flight.controller.abort(); } };
  }, [effectiveToken, load]);

  return <section aria-label={source.label} style={{ paddingTop: 10, marginTop: 10, borderTop: `1px solid ${C.rule}` }}>
    <strong>{source.label}</strong>
    <p>Existing owner: {source.owner} · Series: {source.series}</p>
    {busy && <p role="status">Reading retained GDP evidence…</p>}
    {error && <p role="alert">{error}{latest ? ' Showing the last confirmed retained record.' : ''}</p>}
    {latest && <>
      <p style={{ color: C.text, fontFamily: MONO }}>Latest retained: {show(latest.source_native_value ?? latest.native_value ?? latest.value)} · {latest.unit}</p>
      <p>Reference period: {latest.reference_period}{latest.source_reference_label ? ` · Source reference label: ${latest.source_reference_label}` : ''}</p>
      <p>Published: {show(latest.publication_at)} · Retrieved: {show(latest.retrieved_at, 'Not retained')}</p>
      <p>Source: <a href={latest.source_url} target="_blank" rel="noreferrer" style={{ color: C.amber }}>{latest.publisher}</a></p>
      <p>Field: {source.field_id} · Origin: {source.origin}</p>
      <p>Retained history records shown: {rows.length} / {last.total}.</p>
      <details><summary>Retained history, exact source records and provenance</summary>
        <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 10 }}>{JSON.stringify(pages, null, 2)}</pre>
      </details>
      {last.next_offset !== null && <button type="button" disabled={busy} style={buttonStyle}
        onClick={() => { void load(last.next_offset!); }}>Read older {source.owner} GDP history</button>}
    </>}
    {!busy && !error && !latest && <p>No retained observation is available for this exact field.</p>}
    <button type="button" disabled={busy} style={{ ...buttonStyle, marginTop: 6 }} onClick={() => { void load(); }}>Refresh {source.label}</button>
  </section>;
}

export function InputsUsdGdpEvidence({ token }: { token?: string | null }) {
  const [open, setOpen] = useState(false);
  return <details onToggle={event => setOpen(event.currentTarget.open)} style={{ marginTop: 8, padding: '8px 9px',
    color: C.textSub, background: C.chipOffBg, border: `1px solid ${C.chipOffBd}`, borderRadius: 6,
    fontSize: 11, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
    <summary style={{ cursor: 'pointer', color: C.amber }}>Stored official USD GDP evidence</summary>
    {open && <>
      <p>GDPNow remains the installed Atlanta Fed nowcast. These published rate and level records are retained supporting evidence with their original units.</p>
      <p>Values are labelled latest retained; current-release eligibility is not inferred here.</p>
      <RetainedSeries kind="rate" token={token} /><RetainedSeries kind="level" token={token} />
    </>}
  </details>;
}
